/**
 * The interpreter.
 *
 * Holds a position, walks the script, and blocks on each `await` until the learner plays
 * a move that satisfies it. No React, no I/O and no socket, so the whole thing is
 * testable — which matters more than it sounds, because a tutorial that has quietly
 * broken is worse than no tutorial. The 2023 build shipped one that could not run at all
 * and nothing noticed.
 *
 * Two behaviours are inherited from the 2020 script on purpose:
 *
 *   - the opponent never replies, so it is always the learner's turn; and
 *   - a legal move that is *not* the one being waited for is still played.
 *
 * The second matters. A task like "capture that pawn" may take several moves to walk into
 * position, and refusing everything but the final one would make the board feel broken.
 * Wandering can cost you the position, so every task can be reset to its own setup.
 */

import {
  clonePosition,
  type FastPosition,
  fromPosition,
  getBoard,
  isLegal,
  legalMoves,
  makeMove,
  makePosition,
  type Move,
  packFromMove,
  type PieceCode,
  type PlayerIndex,
  type Variant,
} from '@gok/engine';

import {
  isAwait,
  isSay,
  isSetup,
  isWait,
  type MovePattern,
  satisfies,
  type Setup,
  type Step,
} from './steps.ts';

/** The learner always plays this side. */
export const LEARNER: PlayerIndex = 0;

export const TUTORIAL_VARIANT: Variant = {
  radius: 5,
  formation: 'monarchy',
  spawnsAvailable: 12,
  timeInitialMs: 600_000,
  timeIncrementMs: 5_000,
  stakes: 0,
};

export interface Line {
  readonly id: number;
  readonly text: string;
}

export interface TutorialView {
  readonly position: FastPosition;
  readonly transcript: readonly Line[];
  /** The task being waited on, or `undefined` when the script is running or finished. */
  readonly awaiting: MovePattern | undefined;
  readonly nudge: string | undefined;
  /** Cells to ring, once the hint is showing. */
  readonly hint: readonly number[];
  readonly legalMoves: readonly Move[];
  readonly finished: boolean;
  /** Completed tasks over total tasks — steps are a poor progress measure, tasks are not. */
  readonly progress: { readonly done: number; readonly total: number };
}

export interface Timers {
  schedule(fn: () => void, ms: number): number;
  cancel(handle: number): void;
}

const REAL_TIMERS: Timers = {
  schedule: (fn, ms) => setTimeout(fn, ms) as unknown as number,
  cancel: (handle) => clearTimeout(handle),
};

/** How long a learner is left alone before the hint appears unasked. */
export const DEFAULT_NUDGE_MS = 12_000;

export class TutorialRun {
  private position: FastPosition;
  private cursor = 0;
  private lines: Line[] = [];
  private nextLineId = 1;
  private pending: MovePattern | undefined;
  private pendingHint: readonly string[] = [];
  private pendingNudge: string | undefined;
  private hintVisible = false;
  private timer: number | undefined;
  /** The most recent setup, so a task can be put back the way it started. */
  private lastSetup: Setup | undefined;
  private tasksDone = 0;

  private readonly board = getBoard(TUTORIAL_VARIANT.radius);
  private readonly totalTasks: number;

  constructor(
    private readonly script: readonly Step[],
    private readonly onChange: () => void,
    private readonly timers: Timers = REAL_TIMERS,
  ) {
    this.totalTasks = script.filter(isAwait).length;
    this.position = this.emptyPosition();
    this.run();
  }

  get view(): TutorialView {
    return {
      position: this.position,
      transcript: this.lines,
      awaiting: this.pending,
      nudge: this.hintVisible ? this.pendingNudge : undefined,
      hint: this.hintVisible ? this.resolveHint() : [],
      legalMoves: this.pending ? legalMoves(this.position) : [],
      finished: this.pending === undefined && this.cursor >= this.script.length,
      progress: { done: this.tasksDone, total: this.totalTasks },
    };
  }

  /**
   * Play a move. Returns whether it advanced the script — a legal move that is not the
   * one being waited for still happens, it just does not count.
   */
  play(move: Move): boolean {
    if (!this.pending) return false;
    if (!isLegal(this.position, move)) return false;

    const before = clonePosition(this.position);
    makeMove(this.position, packFromMove(move));
    // The opponent never replies, so it comes straight back round to the learner.
    this.position.playerToMove = LEARNER;

    const matched = satisfies(this.pending, move, {
      indexOf: (key) => this.board.indexOf(key),
      adjacent: (from, to) => this.isAdjacent(from, to),
      occupantBefore: (cell) => before.cells[cell],
    });

    if (matched) {
      this.clearTimer();
      this.clearTask();
      this.tasksDone++;
      this.cursor++;
      this.run();
    } else {
      // They are exploring, or lost. Bring the hint forward rather than waiting out the
      // full timer again.
      this.showHint();
    }

    this.onChange();
    return matched;
  }

  /**
   * Forget the task that was being waited on, hint and nudge included.
   *
   * `run` overwrites all three when it reaches the next task, so for a while this was
   * only clearing them a moment early. It is not: the last task has no next one, and
   * without this the finished tutorial sat under an orange instruction telling the
   * learner to make a move they had already made, with its hint still ringed on a board
   * that had moved on.
   */
  private clearTask(): void {
    this.pending = undefined;
    this.pendingHint = [];
    this.pendingNudge = undefined;
    this.hintVisible = false;
  }

  /** Put the current task back the way it started. */
  resetTask(): void {
    if (this.lastSetup) this.applySetup(this.lastSetup);
    this.onChange();
  }

  /** Show the hint now, rather than waiting for the nudge timer. */
  showHint(): void {
    this.clearTimer();
    this.hintVisible = true;
    this.onChange();
  }

  restart(): void {
    this.clearTimer();
    this.clearTask();
    this.position = this.emptyPosition();
    this.cursor = 0;
    this.lines = [];
    this.lastSetup = undefined;
    this.tasksDone = 0;
    this.run();
    this.onChange();
  }

  dispose(): void {
    this.clearTimer();
  }

  // --- internals ----------------------------------------------------------

  /** Walk the script until it blocks on a task, needs to wait, or runs out. */
  private run(): void {
    while (this.cursor < this.script.length) {
      const step = this.script[this.cursor];

      if (isSay(step)) {
        this.lines.push({ id: this.nextLineId++, text: step.say });
        this.cursor++;
        continue;
      }

      if (isSetup(step)) {
        this.applySetup(step.setup);
        this.lastSetup = step.setup;
        this.cursor++;
        continue;
      }

      if (isWait(step)) {
        this.cursor++;
        this.clearTimer();
        this.timer = this.timers.schedule(() => {
          this.timer = undefined;
          this.run();
          this.onChange();
        }, step.wait);
        return;
      }

      // A task. Stop here until `play` is satisfied.
      this.pending = step.await;
      this.pendingHint = step.hint ?? [];
      this.pendingNudge = step.nudge;
      this.hintVisible = false;
      this.clearTimer();
      if (this.pendingHint.length > 0 || step.nudge) {
        this.timer = this.timers.schedule(() => {
          this.timer = undefined;
          this.hintVisible = true;
          this.onChange();
        }, step.nudgeAfterMs ?? DEFAULT_NUDGE_MS);
      }
      return;
    }
  }

  private applySetup(setup: Setup): void {
    const cells: Record<string, PieceCode> = setup.reset ? {} : this.describeCurrent();

    for (const [key, code] of Object.entries(setup.cells)) cells[key] = code;

    const spawns = setup.spawns ??
      [this.position.spawns[0], this.position.spawns[1]] as const;

    this.position = fromPosition(
      makePosition({
        variant: TUTORIAL_VARIANT,
        playerToMove: LEARNER,
        spawnsAvailable: [spawns[0], spawns[1]],
        pieces: cells,
      }),
    );
  }

  private describeCurrent(): Record<string, PieceCode> {
    const out: Record<string, PieceCode> = {};
    for (let i = 0; i < this.position.cells.length; i++) {
      const piece = this.position.cells[i];
      if (piece === 0) continue;
      const king = (piece & 0b100) === 0;
      const owner = (piece >> 1) & 1;
      out[this.board.keyAt(i)] = `${king ? 'k' : 'p'}${owner}` as PieceCode;
    }
    return out;
  }

  private emptyPosition(): FastPosition {
    return fromPosition(
      makePosition({
        variant: TUTORIAL_VARIANT,
        playerToMove: LEARNER,
        spawnsAvailable: [
          TUTORIAL_VARIANT.spawnsAvailable,
          TUTORIAL_VARIANT.spawnsAvailable,
        ],
        pieces: {},
      }),
    );
  }

  private resolveHint(): number[] {
    const cells: number[] = [];
    for (const key of this.pendingHint) {
      const index = this.board.indexOf(key);
      if (index !== undefined) cells.push(index);
    }
    return cells;
  }

  private isAdjacent(from: number, to: number): boolean {
    for (let dir = 0; dir < 6; dir++) {
      if (this.board.neighborOf(from, dir) === to) return true;
    }
    return false;
  }

  private clearTimer(): void {
    if (this.timer !== undefined) {
      this.timers.cancel(this.timer);
      this.timer = undefined;
    }
  }
}
